import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import { api, API } from "@/lib/api";
import { Waveform, ArrowLeft, CalendarBlank } from "@phosphor-icons/react";
import { HollywoodCube } from "@/components/HollywoodCube";
import { NetworkStrip } from "@/pages/Network";

const OG_IMAGE = "/og-blog.jpg";
const coverUrl = (a) => (a?.cover_path ? `${API}/media/${a.cover_path}` : null);

function BlogShell({ children }) {
  return (
    <div className="min-h-screen">
      <header className="border-b border-border sticky top-0 glass z-10">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/blog" className="flex items-center gap-2">
            <Waveform size={24} weight="fill" className="text-primary" />
            <span className="font-head font-bold text-xl tracking-tighter gold-foil">SMI</span>
            <span className="overline text-muted-foreground ml-1">JOURNAL</span>
          </Link>
          <div className="flex items-center gap-5">
            <Link to="/network" className="text-sm text-muted-foreground hover:text-white transition-colors duration-200">Network</Link>
            <Link to="/" className="text-sm text-muted-foreground hover:text-white transition-colors duration-200">Dashboard</Link>
          </div>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-6 py-10">{children}</div>
    </div>
  );
}

export function BlogIndex() {
  const [articles, setArticles] = useState(null);
  useEffect(() => { api.get("/blog").then(({ data }) => setArticles(data)).catch(() => setArticles([])); }, []);

  return (
    <BlogShell>
      <Helmet>
        <title>The SMI Journal — Reputation & Brand Insights</title>
        <meta name="description" content="Positive, high-authority articles auto-published by the SMI SEO engine." />
        <link rel="canonical" href={`${window.location.origin}/blog`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="The SMI Journal" />
        <meta property="og:description" content="Positive, high-authority content published by Social Media Influencer." />
        <meta property="og:image" content={`${window.location.origin}${OG_IMAGE}`} />
        <meta property="og:url" content={`${window.location.origin}/blog`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The SMI Journal" />
        <meta name="twitter:image" content={`${window.location.origin}${OG_IMAGE}`} />
      </Helmet>
      <div className="mb-10 flex items-start justify-between gap-6">
        <div>
          <div className="overline text-primary mb-2">SMI · BUILD YOUR LEGACY · 24/7</div>
          <h1 className="font-head font-bold text-5xl sm:text-6xl tracking-tighter"><span className="gold-foil">The SMI Journal</span></h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">Reputation & brand-building intelligence from SMI — the platform that helps artists, entrepreneurs, influencers, celebrities and business owners own their name across the web.</p>
        </div>
        <div className="hidden md:block pt-2"><HollywoodCube size={100} /></div>
      </div>
      <NetworkStrip />
      {articles === null && <div className="text-muted-foreground font-mono text-sm">Loading…</div>}
      {articles?.length === 0 && (
        <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          No SMI articles published yet.
        </div>
      )}
      <div className="space-y-5">
        {articles?.map((a) => (
          <Link key={a.id} to={`/blog/${a.slug}`} data-testid={`blog-card-${a.slug}`}
            className="block relative border border-border hover:border-primary transition-colors duration-200 group overflow-hidden">
            <div className="absolute top-3 right-3 z-10 opacity-80 group-hover:opacity-100 transition-opacity duration-200"><HollywoodCube size={34} /></div>
            {coverUrl(a) && (
              <div className="aspect-[16/9] overflow-hidden border-b border-border">
                <img src={coverUrl(a)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
            )}
            <div className="p-6">
              <div className="overline text-muted-foreground mb-2">{a.brand} · {a.platform}</div>
              <h2 className="font-head font-bold text-xl group-hover:text-primary transition-colors duration-200 normal-case">{a.title}</h2>
              <p className="text-sm text-muted-foreground mt-2">{a.meta_description}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {a.tags?.slice(0, 5).map((t, i) => <span key={i} className="font-mono text-xs text-primary">#{t}</span>)}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </BlogShell>
  );
}

export function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    api.get(`/blog/${slug}`).then(({ data }) => setArticle(data)).catch(() => setNotFound(true));
  }, [slug]);

  return (
    <BlogShell>
      {article && (
        <Helmet>
          <title>{`${article.title} — SMI Journal`}</title>
          <meta name="description" content={article.meta_description || ""} />
          <meta name="keywords" content={(article.tags || []).join(", ")} />
          <link rel="canonical" href={`${window.location.origin}/blog/${article.slug}`} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={article.title} />
          <meta property="og:description" content={article.meta_description || ""} />
          <meta property="og:image" content={coverUrl(article) || `${window.location.origin}${OG_IMAGE}`} />
          <meta property="og:url" content={`${window.location.origin}/blog/${article.slug}`} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={article.title} />
          <meta name="twitter:description" content={article.meta_description || ""} />
          <meta name="twitter:image" content={coverUrl(article) || `${window.location.origin}${OG_IMAGE}`} />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              headline: article.title,
              description: article.meta_description,
              datePublished: article.published_at,
              keywords: (article.tags || []).join(", "),
              author: { "@type": "Organization", name: article.brand || "Social Media Influencer" },
              publisher: { "@type": "Organization", name: "Social Media Influencer" },
              image: coverUrl(article) || `${window.location.origin}${OG_IMAGE}`,
              mainEntityOfPage: `${window.location.origin}/blog/${article.slug}`,
            })}
          </script>
        </Helmet>
      )}
      <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-white mb-6 transition-colors duration-200">
        <ArrowLeft size={16} /> All articles
      </Link>
      {notFound && <div className="text-muted-foreground">Article not found.</div>}
      {!article && !notFound && <div className="text-muted-foreground font-mono text-sm">Loading…</div>}
      {article && (
        <article data-testid="blog-article">
          <div className="overline text-accent mb-3">{article.brand} · {article.platform}</div>
          <h1 className="font-head font-bold text-4xl sm:text-5xl tracking-tighter leading-tight normal-case">{article.title}</h1>
          {(article.primary_keyword || article.tags?.length > 0) && (
            <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="article-seo-keywords">
              <span className="overline text-muted-foreground">OPTIMIZED FOR</span>
              {article.primary_keyword && <span className="font-mono text-xs px-2 py-1 border border-primary/40 bg-primary/10 text-primary">{article.primary_keyword}</span>}
              {(article.tags || []).slice(0, 5).map((t, i) => (
                <span key={i} className="font-mono text-xs px-2 py-1 border border-border bg-secondary text-muted-foreground">{t}</span>
              ))}
            </div>
          )}
          {article.published_at && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-4">
              <CalendarBlank size={14} /> {article.published_at.slice(0, 10)}
            </div>
          )}
          {coverUrl(article) && (
            <div className="mt-8 border border-border overflow-hidden aspect-[16/9]">
              <img src={coverUrl(article)} alt={article.title} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="prose-aura mt-8 text-[15px] leading-relaxed text-zinc-200 space-y-4">
            <ReactMarkdown
              components={{
                h2: ({ node, ...p }) => <h2 className="font-head font-bold text-2xl mt-8 mb-3 text-white normal-case" {...p} />,
                h3: ({ node, ...p }) => <h3 className="font-head font-semibold text-xl mt-6 mb-2 text-white normal-case" {...p} />,
                p: ({ node, ...p }) => <p className="mb-4" {...p} />,
                ul: ({ node, ...p }) => <ul className="list-disc pl-5 space-y-1 mb-4" {...p} />,
                ol: ({ node, ...p }) => <ol className="list-decimal pl-5 space-y-1 mb-4" {...p} />,
                strong: ({ node, ...p }) => <strong className="text-white font-semibold" {...p} />,
                a: ({ node, ...p }) => <a className="text-primary underline" {...p} />,
              }}
            >
              {article.body}
            </ReactMarkdown>
          </div>
          <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-border">
            {article.tags?.map((t, i) => (
              <span key={i} className="font-mono text-xs px-2 py-1 border border-border bg-secondary text-muted-foreground">#{t}</span>
            ))}
          </div>
        </article>
      )}
    </BlogShell>
  );
}
